import { useCallback, useMemo, useState } from 'react';

import { Button, DatePicker, Select } from 'antd';
import type { PickerLocale } from 'antd/es/date-picker/generatePicker';
import enUS from 'antd/es/date-picker/locale/en_US';
import esES from 'antd/es/date-picker/locale/es_ES';
import type { Moment } from 'moment';
import moment from 'moment';

import { useIntl } from 'react-intl';

import ElasticPaginatedSelect from '@src/components/ElasticPaginatedSelect';
import type { ElasticPaginatedSelectOption } from '@src/components/ElasticPaginatedSelect/ElasticPaginatedSelect.interfaces';
import { FSCollections } from '@src/constants/database';
import type { ElasticSearchResponse } from '@src/models/interfaces/general/ElasticSearchResponseInterface';
import { formatUserFullName } from '@src/util/format/userFullName';
import IntlMessages from '@src/util/IntlMessages';

import {
  WORK_JOURNEY_MANAGEMENT_USER_FILTER_DEBOUNCE_MS,
  WORK_JOURNEY_MANAGEMENT_USER_FILTER_MIN_SEARCH_LENGTH,
} from '../../constants';
import type {
  IWorkJourneyManagementFilterOption,
  IWorkJourneyManagementFilters,
  TWorkJourneyManagementFilterStatus,
} from '../../utils/workJourneyManagementFilters';
import {
  getLimitedJourneyFilterOptions,
  hasActiveFilters,
  WORK_JOURNEY_MANAGEMENT_FILTER_ALL,
  WORK_JOURNEY_MANAGEMENT_FILTER_STATUSES,
} from '../../utils/workJourneyManagementFilters';
import styles from './styles.module.less';

const { MonthPicker } = DatePicker;

const isSpanishLocale = (intlLocale: string): boolean =>
  intlLocale.toLowerCase().startsWith('es');

/** Short month labels for the panel — do not rely on moment (often stays English in the bundle). */
const getShortMonthLabels = (intlLocale: string): string[] => {
  const locale = isSpanishLocale(intlLocale) ? 'es' : 'en';
  return Array.from({ length: 12 }, (_, monthIndex) =>
    new Intl.DateTimeFormat(locale, { month: 'short' }).format(new Date(2020, monthIndex, 1)),
  );
};

const getMonthPickerLocale = (intlLocale: string): PickerLocale => {
  const isSpanish = isSpanishLocale(intlLocale);
  const base = isSpanish ? esES : enUS;
  return {
    ...base,
    lang: {
      ...base.lang,
      locale: isSpanish ? 'es' : 'en',
      shortMonths: getShortMonthLabels(intlLocale),
    },
  };
};

type ElasticUserSource = {
  firstName?: string;
  lastName?: string;
  email?: string;
};

interface WorkJourneyManagementFiltersProps {
  filters: IWorkJourneyManagementFilters;
  organizationId: string;
  /** Shared visible month for journeys + calendar (1–12). */
  month: number;
  year: number;
  shiftOptions: IWorkJourneyManagementFilterOption[];
  journeyOptions: IWorkJourneyManagementFilterOption[];
  onChange: (filters: Partial<IWorkJourneyManagementFilters>) => void;
  onUserOptionResolved: (option: IWorkJourneyManagementFilterOption | null) => void;
  /** Jump journeys/calendar to this month (not a client-side filter). */
  onMonthYearChange: (month: number, year: number) => void;
  onReset: () => void;
}

const parseUserOptions = (
  response: ElasticSearchResponse<ElasticUserSource>,
): ElasticPaginatedSelectOption[] =>
  (response?.hits?.hits ?? []).map(hit => {
    const source = (hit._source ?? {}) as ElasticUserSource;
    const value = String(hit._id ?? '');
    const label = formatUserFullName(source) || source.email || value;
    return { value, label };
  });

export const WorkJourneyManagementFilters = ({
  filters,
  organizationId,
  month,
  year,
  shiftOptions,
  journeyOptions,
  onChange,
  onUserOptionResolved,
  onMonthYearChange,
  onReset,
}: WorkJourneyManagementFiltersProps) => {
  const intl = useIntl();
  const [journeySearch, setJourneySearch] = useState('');

  // Each "all" option names its own axis: antd 4 selects have no visible label.
  const allOption = (id: string) => ({
    value: WORK_JOURNEY_MANAGEMENT_FILTER_ALL,
    label: intl.formatMessage({ id }),
  });

  const statusOptions = [
    allOption('workJourney.management.filters.allStatuses'),
    ...WORK_JOURNEY_MANAGEMENT_FILTER_STATUSES.map(status => ({
      value: status,
      label: intl.formatMessage({ id: `workJourney.management.journeyStatus.${status}` }),
    })),
  ];

  const limitedJourneyOptions = useMemo(
    () => getLimitedJourneyFilterOptions(journeyOptions, journeySearch, filters.journeyId),
    [filters.journeyId, journeyOptions, journeySearch],
  );

  const monthPickerLocale = useMemo(
    () => getMonthPickerLocale(intl.locale),
    [intl.locale],
  );

  const monthValue = useMemo(
    () => moment({ year, month: month - 1, day: 1 }),
    [month, year],
  );

  /** Input label via react-intl — antd `MMMM` follows ConfigProvider/moment, not always UI locale. */
  const formatMonthYear = useCallback(
    (value: Moment) =>
      intl.formatDate(value.toDate(), { month: 'long', year: 'numeric' }),
    [intl],
  );

  const handleMonthChange = (value: Moment | null) => {
    if (!value) return;
    onMonthYearChange(value.month() + 1, value.year());
  };

  const handleJourneyChange = (journeyId: string) => {
    setJourneySearch('');
    onChange({ journeyId });
  };

  const handleUserChange = useCallback(
    (
      value: string | string[] | undefined,
      option: ElasticPaginatedSelectOption | ElasticPaginatedSelectOption[] | undefined,
    ) => {
      const userId =
        typeof value === 'string' && value ? value : WORK_JOURNEY_MANAGEMENT_FILTER_ALL;
      onChange({ userId });

      if (userId === WORK_JOURNEY_MANAGEMENT_FILTER_ALL || !option || Array.isArray(option)) {
        onUserOptionResolved(null);
        return;
      }

      onUserOptionResolved({
        value: userId,
        label: typeof option.label === 'string' ? option.label : userId,
      });
    },
    [onChange, onUserOptionResolved],
  );

  const userSelectValue =
    filters.userId === WORK_JOURNEY_MANAGEMENT_FILTER_ALL ? undefined : filters.userId;

  return (
    <div className={styles.filtersPanel} data-testid="work-journey-management-filters">
      <label className={styles.field}>
        <span className={styles.fieldLabel}>
          <IntlMessages id="workJourney.management.filters.status" />
        </span>
        <Select
          className={styles.control}
          data-testid="filter-status"
          value={filters.status}
          options={statusOptions}
          onChange={(status: TWorkJourneyManagementFilterStatus) => onChange({ status })}
        />
      </label>

      <label className={styles.field}>
        <span className={styles.fieldLabel}>
          <IntlMessages id="workJourney.management.filters.jumpToMonth" />
        </span>
        <MonthPicker
          className={styles.range}
          data-testid="filter-month-year"
          value={monthValue}
          locale={monthPickerLocale}
          format={formatMonthYear}
          allowClear={false}
          placeholder={intl.formatMessage({
            id: 'workJourney.management.filters.jumpToMonthPlaceholder',
          })}
          onChange={handleMonthChange}
        />
      </label>

      <label className={styles.field}>
        <span className={styles.fieldLabel}>
          <IntlMessages id="workJourney.management.filters.user" />
        </span>
        <div data-testid="filter-user">
          <ElasticPaginatedSelect<ElasticUserSource>
            className={styles.control}
            entity={FSCollections.USERS}
            organizationId={organizationId}
            searchKey={['firstName', 'lastName', 'email']}
            debounceMs={WORK_JOURNEY_MANAGEMENT_USER_FILTER_DEBOUNCE_MS}
            minSearchLength={WORK_JOURNEY_MANAGEMENT_USER_FILTER_MIN_SEARCH_LENGTH}
            pageSize={10}
            value={userSelectValue}
            allowClear
            placeholder={intl.formatMessage({
              id: 'workJourney.management.filters.userSearchPlaceholder',
            })}
            parseData={parseUserOptions}
            onChange={handleUserChange}
          />
        </div>
      </label>

      <label className={styles.field}>
        <span className={styles.fieldLabel}>
          <IntlMessages id="workJourney.management.filters.shift" />
        </span>
        <Select
          className={styles.control}
          data-testid="filter-shift"
          value={filters.shift}
          options={[allOption('workJourney.management.filters.allShifts'), ...shiftOptions]}
          onChange={(shift: string) => onChange({ shift })}
        />
      </label>

      <label className={styles.field}>
        <span className={styles.fieldLabel}>
          <IntlMessages id="workJourney.management.filters.journey" />
        </span>
        <Select
          className={styles.control}
          data-testid="filter-journey"
          value={filters.journeyId}
          showSearch
          filterOption={false}
          onSearch={setJourneySearch}
          options={[
            allOption('workJourney.management.filters.allJourneys'),
            ...limitedJourneyOptions,
          ]}
          onChange={handleJourneyChange}
          onBlur={() => setJourneySearch('')}
        />
      </label>

      {hasActiveFilters(filters) && (
        <Button type="link" className={styles.reset} onClick={onReset} data-testid="filter-reset">
          <IntlMessages id="workJourney.management.filters.clear" />
        </Button>
      )}
    </div>
  );
};
